/**
 * Trinket effects (PLAN §11 "Trinkets": most have an on-use or proc effect, a second, smaller
 * layer of choice). Pure data, applied by compileCharacter as ClassTransforms.
 *
 * Class-generic data and the shared registry: the sim registry indexes auras by id and the
 * FIRST definition wins, so an aura that several classes add must be byte-identical across
 * classes. That rules out `@bind:Q` inside a shared aura (it resolves to a different ability
 * per class, and every class after the first would silently get the first class's version).
 * Instead `everyBind('Q')` lists every class's Q ability: a proc filter only matches the
 * holder's own casts, and `modifyCooldown` on an ability the unit does not own is a no-op.
 *
 * "On-use" here means "fires when you press your <key> ability" (with an internal cooldown),
 * so the effect needs no extra button and bots use it for free with their normal rotation.
 *
 * Amounts are power coefficients or % of max HP, so trinkets scale with item level through
 * the character's power and HP (compile's `power × powerMult(avgIlvl)`).
 */
import { getContentBundle } from '@keystone/content';
import type { AbilityId, AuraDef, BindKey, Role } from '@keystone/sim';
import type { ClassTransform, TrinketEffectDef } from './types';

/** Every class's ability on `key`, in bundle order (deterministic). */
export function everyBind(key: BindKey): AbilityId[] {
  const out: AbilityId[] = [];
  for (const c of getContentBundle().classes) {
    const id = c.binds[key];
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/** A permanent, hidden carrier aura (compile also forces persistThroughDeath). */
export function hiddenAura(id: string, name: string, extra: Partial<AuraDef>): AuraDef {
  return { id, name, kind: 'buff', durationMs: 0, stacking: 'unique', flags: { hidden: true, persistThroughDeath: true }, ...extra };
}

/** A visible timed buff on the holder. */
export function timedBuff(id: string, name: string, durationMs: number, extra: Partial<AuraDef>): AuraDef {
  return { id, name, kind: 'buff', durationMs, stacking: 'refresh', ...extra };
}

/** A hidden "recharging" marker used to gate conditional procs (proc ICDs fire even when a condition fails). */
export function marker(id: string, name: string, durationMs: number): AuraDef {
  return { id, name: `${name} (recharging)`, kind: 'debuff', durationMs, stacking: 'ignore', flags: { hidden: true } };
}

const passive = (aura: AuraDef): ClassTransform => ({ op: 'addPassive', aura });

const trinket = (id: string, name: string, description: string, effect: ClassTransform[], roles?: Role[]): TrinketEffectDef =>
  roles ? { id, name, description, effect, roles } : { id, name, description, effect };

export const TRINKETS: readonly TrinketEffectDef[] = [
  trinket(
    'trinket.venom_fang',
    'Venom Fang',
    'Your damage has a 10% chance to deal extra nature damage (2 s internal cooldown).',
    [
      passive(
        hiddenAura('trinket.venom_fang', 'Venom Fang', {
          procs: [{ on: 'damageDealt', chance: 0.1, icdMs: 2000, effects: [{ type: 'damage', school: 'nature', amount: { coef: 1.5 } }] }],
        }),
      ),
    ],
    ['dps', 'tank'],
  ),
  trinket(
    'trinket.ember_idol',
    'Ember Idol',
    'On use (with your Q ability): +12% power for 15 s. 60 s cooldown.',
    [
      passive(
        hiddenAura('trinket.ember_idol', 'Ember Idol', {
          procs: [
            {
              on: 'cast',
              abilityIds: everyBind('Q'),
              icdMs: 60000,
              effects: [{ type: 'applyAura', to: 'self', aura: timedBuff('trinket.ember_idol.buff', 'Ember Idol', 15000, { mods: [{ stat: 'powerPct', value: 0.12 }] }) }],
            },
          ],
        }),
      ),
    ],
  ),
  trinket(
    'trinket.stormglass_prism',
    'Stormglass Prism',
    'Your critical strikes have a 20% chance to grant +12% haste for 8 s (20 s internal cooldown).',
    [
      passive(
        hiddenAura('trinket.stormglass_prism', 'Stormglass Prism', {
          procs: [
            {
              on: 'critDealt',
              chance: 0.2,
              icdMs: 20000,
              effects: [{ type: 'applyAura', to: 'self', aura: timedBuff('trinket.stormglass_prism.buff', 'Stormglass Surge', 8000, { mods: [{ stat: 'hastePct', value: 0.12 }] }) }],
            },
          ],
        }),
      ),
    ],
    ['dps', 'healer'],
  ),
  trinket(
    'trinket.wardstone',
    'Wardstone',
    'Taking damage below 50% health shields you for 12% of your max HP. 45 s cooldown.',
    [
      passive(
        hiddenAura('trinket.wardstone', 'Wardstone', {
          procs: [
            {
              on: 'damageTaken',
              effects: [
                {
                  type: 'conditional',
                  cond: { type: 'and', all: [{ type: 'selfHpBelow', pct: 0.5 }, { type: 'selfLacksAura', auraId: 'trinket.wardstone.cd' }] },
                  then: [
                    { type: 'absorb', to: 'self', auraId: 'trinket.wardstone.shield', amount: { pctSourceMaxHp: 0.12 } },
                    { type: 'applyAura', to: 'self', aura: marker('trinket.wardstone.cd', 'Wardstone', 45000) },
                  ],
                },
              ],
            },
          ],
        }),
      ),
      { op: 'registerAura', aura: { id: 'trinket.wardstone.shield', name: 'Wardstone', kind: 'buff', durationMs: 10000, stacking: 'refresh', absorb: {} } },
    ],
    ['tank', 'dps'],
  ),
  trinket(
    'trinket.lifebinder_seed',
    'Lifebinder Seed',
    'Your heals have a 15% chance to plant a seed that heals the target every second for 6 s (3 s internal cooldown).',
    [
      passive(
        hiddenAura('trinket.lifebinder_seed', 'Lifebinder Seed', {
          procs: [
            {
              on: 'healDealt',
              chance: 0.15,
              icdMs: 3000,
              effects: [
                {
                  type: 'applyAura',
                  aura: {
                    id: 'trinket.lifebinder_seed.hot',
                    name: 'Lifebinder Seed',
                    kind: 'buff',
                    durationMs: 6000,
                    stacking: 'refresh',
                    periodic: { intervalMs: 1000, hasteScaled: true, effects: [{ type: 'heal', amount: { coef: 0.35 } }] },
                  },
                },
              ],
            },
          ],
        }),
      ),
    ],
    ['healer'],
  ),
  trinket(
    'trinket.hourglass_of_echoes',
    'Hourglass of Echoes',
    'Using your R ability (interrupt or dispel) reduces the cooldown of your Q ability by 4 s.',
    [
      passive(
        hiddenAura('trinket.hourglass_of_echoes', 'Hourglass of Echoes', {
          procs: [{ on: 'cast', abilityIds: everyBind('R'), effects: everyBind('Q').map((abilityId) => ({ type: 'modifyCooldown' as const, to: 'self' as const, abilityId, deltaMs: -4000 })) }],
        }),
      ),
    ],
  ),
  trinket(
    'trinket.sanguine_charm',
    'Sanguine Charm',
    'Your damage has an 8% chance to heal you for 2% of your max HP (3 s internal cooldown).',
    [
      passive(
        hiddenAura('trinket.sanguine_charm', 'Sanguine Charm', {
          procs: [{ on: 'damageDealt', chance: 0.08, icdMs: 3000, effects: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.02 } }] }],
        }),
      ),
    ],
    ['tank', 'dps'],
  ),
  trinket(
    'trinket.quickening_sand',
    'Quickening Sand',
    'On use (with your E ability): +18% haste for 12 s. 60 s cooldown.',
    [
      passive(
        hiddenAura('trinket.quickening_sand', 'Quickening Sand', {
          procs: [
            {
              on: 'cast',
              abilityIds: everyBind('E'),
              icdMs: 60000,
              effects: [{ type: 'applyAura', to: 'self', aura: timedBuff('trinket.quickening_sand.buff', 'Quickening Sand', 12000, { mods: [{ stat: 'hastePct', value: 0.18 }] }) }],
            },
          ],
        }),
      ),
    ],
  ),
  trinket(
    'trinket.moonlit_censer',
    'Moonlit Censer',
    'On use (with your F ability): you take 15% less damage and heal for 1% of your max HP every second for 8 s. 90 s cooldown.',
    [
      passive(
        hiddenAura('trinket.moonlit_censer', 'Moonlit Censer', {
          procs: [
            {
              on: 'cast',
              abilityIds: everyBind('F'),
              icdMs: 90000,
              effects: [
                {
                  type: 'applyAura',
                  to: 'self',
                  aura: timedBuff('trinket.moonlit_censer.buff', 'Moonlit Censer', 8000, {
                    mods: [{ stat: 'damageTakenPct', value: -0.15 }],
                    periodic: { intervalMs: 1000, effects: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.01 } }] },
                  }),
                },
              ],
            },
          ],
        }),
      ),
    ],
  ),
];

/** Trinkets a role may roll (the item generator only rolls usable ones). */
export function trinketsForRole(role: Role | undefined): TrinketEffectDef[] {
  return TRINKETS.filter((t) => !role || !t.roles || t.roles.length === 0 || t.roles.includes(role));
}
